import React, { useState, useEffect, useCallback } from 'react'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { isValidUrl, isValidEmail } from 'utils'
import { Button, PhoneInput } from '@arnsbogroup/react-voicearchive'

const Basics = ({ match: { params: { supplierId } }, setCallback }) => {
  const initialEmptyPhone = {
    number: '',
    country: 'gb'
  }

  const initialState = {
    firstname: '',
    lastname: '',
    url: '',
    email: '',
    phone: initialEmptyPhone,
    mobile: null,
  }


  const { formatMessage } = useIntl()
  const [state, setState] = useState(initialState)

  const [errors, setErrors] = useState(
    Object.keys(state).reduce((memo, key) => {
      memo[key] = true
      return memo
    }, {})
  )

  useEffect(() => {
    fetch(`/rest/v2/supplier/${supplierId}`, { credentials: 'same-origin' })
      .then((response) => response.json())
      .then((supplier) => {
        setState((prev) => {
          const next = { ...prev }
          Object.keys(prev).forEach((key) => {
            if (supplier[key] !== undefined) next[key] = supplier[key]
          })

          next.phone =
            typeof supplier.phone == 'string'
              ? { number: supplier.phone, country: 'gb' }
              : supplier.phone || { number: '', country: 'gb' }

          next.mobile =
            typeof supplier.mobile == 'string' && supplier.mobile && supplier.mobile != ''
              ? { number: supplier.mobile, country: 'gb' }
              : null

          return next
        })
      })
      .catch((error) => {
        window.alert(error)
      })
  }, [])

  useEffect(() => {
    setCallback((event) => new Promise((resolve, reject) => {
      const nextErrors = {
        firstname: !!state.firstname && state.firstname.length > 1,
        lastname: !!state.lastname && state.lastname.length > 1,
        url: state.url !== '' ? isValidUrl(state.url) : true,
        email: !!state.email && isValidEmail(state.email),
        phone: !!state.phone.number && state.phone.number.length > 5,
        mobile: state.mobile && state.mobile.number != '' ? !!state.mobile.number && state.mobile.number.length > 5 : true,
      }

      if (Object.values(nextErrors).some((valid) => !valid)) {
        event.stopPropagation()
        event.preventDefault()
        setErrors(nextErrors)
        return reject(formatMessage(messages.validationFailed))
      }

      const diff = Object.keys(state).reduce((diff, key) => {
        if (initialState[key] === state[key]) return diff
        return { ...diff, [key]: state[key] }
      }, {})

      if (Object.keys(diff).length === 0 && diff.constructor === Object) {
        return
      }

      if (diff.phone) {
        diff.phone = diff.phone.number
      }
      
      if (diff.mobile) {
        diff.mobile = diff.mobile.number
      }

      fetch(`/rest/v2/supplier/${supplierId}`, {
        credentials: 'same-origin',
        method: 'PUT',
        body: JSON.stringify(diff),
      })
        .then(resolve)
        .catch(reject)
    }))
  }, [state, errors])


  const renderInput = useCallback((name, className = "block mt-6", required = true) => {
    return (
      <fieldset className="block">
        <p className="py-4">
          <FormattedMessage {...messages[`${name}Label`] } /> { required && '*' }
        </p>

        <input
          type="text"
          value={state[name]}
          onChange={({ target: { value } }) => setState({ ...state, [name]: value })}
          onBlur={() => setErrors((prevErrors) => ({ ...prevErrors, [name]: true }))}
          placeholder={formatMessage(messages[`${name}Placeholder`])}
          className={`w-full p-4 focus:border-primary transition duration-200 border ${errors[name] ? 'border-gray-400' : 'border-red-600'}`}
        />
      </fieldset>
    )
  }, [state, errors])

  return (
    <div>
      <div className="font-medium text-4xl">
        <FormattedMessage {...messages.title } />
      </div>

      <div className="text-gray-700 mt-6 w-2/3">
        <FormattedMessage {...messages.subtitle } />
      </div>

      <div className="grid grid-cols-2 mt-12 gap-x-6">
        { renderInput('firstname') }
        { renderInput('lastname') }
      </div>

      { renderInput('email') }
      { renderInput('url', null, false) }

      <fieldset className="block mt-6">
        <p className="py-4">
          <FormattedMessage {...messages.phoneNumberPrimaryLabel } /> *
        </p>
        <PhoneInput
          country={initialState.phone.country}
          value={state.phone.number}
          onChange={(phone, country) => {
            const data = {
              number: phone ? '+' + phone : '',
              country: country.countryCode
            }
            setState({ ...state, phone: data })
          }}
          placeholder={formatMessage(messages.phoneNumberPlaceholder)}
          enableSearch
          disableSearchIcon
          containerClass="w-full border border-gray-400 focus:border-primary transition duration-200"
          inputClass="w-full py-4 h-auto"
          inputStyle={{ width: '100%', height: 'auto', border: 'initial' }}
          buttonStyle={{ border: 'initial' }}
          searchPlaceholder={formatMessage(messages.phoneNumberSearchPlaceholder)}
          searchNotFound={formatMessage(messages.phoneNumberNotFoundPlaceholder)}
          tabIndex={0}
        />

      { state.mobile ? (
        <React.Fragment>
          <p className="py-4">
            <FormattedMessage {...messages.phoneNumberSecondaryLabel } /> *
          </p>
          <PhoneInput
            country={state.phone.country || state.mobile.country}
            value={state.mobile.number}
            onChange={(phone, country) => {
              const data = {
                number: phone ? '+' + phone : '',
                country: country.countryCode
              }
              setState({ ...state, mobile: data })
            }}
            placeholder={formatMessage(messages.phoneNumberPlaceholder)}
            enableSearch
            disableSearchIcon
            containerClass="w-full border border-gray-400 focus:border-primary transition duration-200"
            inputClass="w-full py-4 h-auto"
            inputStyle={{ width: '100%', height: 'auto', border: 'initial' }}
            buttonStyle={{ border: 'initial' }}
            searchPlaceholder={formatMessage(messages.phoneNumberSearchPlaceholder)}
            searchNotFound={formatMessage(messages.phoneNumberNotFoundPlaceholder)}
            tabIndex={1}
          />
        </React.Fragment>
      ) : (
        <Button 
          variant="inline"
          size="sm"
          className="float-right" 
          onClick={() => setState({ ...state, mobile: initialEmptyPhone })}
          icon={['fa', 'plus']}
        >
          <FormattedMessage {...messages.phoneNumberAdd } />
        </Button>
      ) }
      </fieldset>
    </div>
  )
}

export default Basics
